import { AutomationType, TriggerList, TriggerListClient } from "helpers/enum";

// ROI-356: job-based and placement-based campaigns get their own Activity trigger ids for Survey opened,
// Survey completed and Form completed. They share the base Activity trigger's form, payload and hover,
// so the builder treats them as that trigger wherever it branches on the trigger id.
//
// Candidate and contact ids overlap (contact 48 / 49 are candidate Job / Placement form score), so every
// check here is scoped to the automation type.

const CandidateJobPlacementActivityIds = [TriggerList.JobRoiAiActivity, TriggerList.PlacementRoiAiActivity];
const ClientJobPlacementActivityIds = [TriggerListClient.ClientJobRoiAiActivity, TriggerListClient.ClientPlacementRoiAiActivity];

const isClient = automationType => automationType === AutomationType.Client;

export const baseActivityTriggerId = automationType =>
  isClient(automationType) ? TriggerListClient.RoiAiActivity : TriggerList.RoiAiActivity;

// True for the job-based or placement-based survey / form Activity trigger of this automation type.
export const isJobPlacementActivityTrigger = (triggerId, automationType) =>
  (isClient(automationType) ? ClientJobPlacementActivityIds : CandidateJobPlacementActivityIds).includes(triggerId);

// True for the base Activity trigger or one of its job-based / placement-based variants.
export const isActivityTrigger = (triggerId, automationType) =>
  triggerId === baseActivityTriggerId(automationType) || isJobPlacementActivityTrigger(triggerId, automationType);

// Maps a job-based / placement-based Activity trigger id to the base Activity id; any other id is returned
// unchanged. Use it as the subject of a switch or comparison written against the base Activity id.
export const toBaseActivityTriggerId = (triggerId, automationType) =>
  isJobPlacementActivityTrigger(triggerId, automationType) ? baseActivityTriggerId(automationType) : triggerId;

// What one survey / form response enters on each job-based and placement-based campaign type (ROI-356).
// `single` is what enters on a placement campaign whose re-entry restriction is on: the engine then keeps
// one placement per person (the highest placement id) after the triggers run. Jobs and applications are
// restricted one by one, so every one of them enters whatever the setting.
const fanOutTarget = (triggerId, automationType) => {
  if (isClient(automationType)) {
    if (triggerId === TriggerListClient.ClientJobRoiAiActivity)
      return { record: "job", entered: "every job they are the main contact for in this campaign" };
    if (triggerId === TriggerListClient.ClientPlacementRoiAiActivity)
      return {
        record: "placement",
        entered: "every placement they are the contact for in this campaign",
        single: "the most recent placement they are the contact for in this campaign"
      };
    return null;
  }
  if (triggerId === TriggerList.JobRoiAiActivity)
    return { record: "job", entered: "every job application they have in this campaign" };
  if (triggerId === TriggerList.PlacementRoiAiActivity)
    return {
      record: "placement",
      entered: "every placement they have in this campaign",
      single: "their most recent placement in this campaign"
    };
  return null;
};

// The picker hint does not know the campaign's re-entry setting, so placement hints say it can narrow the entry.
const PlacementReEntryHint = " Depending on the campaign's re-entry setting, only one placement per person may enter.";

const ActivityVerb = {
  18: "opens a survey",
  19: "completes a survey",
  29: "completes a form",
};

// Hover text for the Survey opened / Survey completed / Form completed picker items on job-based and
// placement-based campaigns. Null on Candidate and Contact campaigns, where nothing fans out.
export const surveyFormActivityHint = (triggerId, automationType, activityId) => {
  const target = fanOutTarget(triggerId, automationType);
  if (!target || !ActivityVerb[activityId]) return null;
  const reEntryHint = target.single ? PlacementReEntryHint : "";
  return `Starts when a person ${ActivityVerb[activityId]}. Surveys and forms are sent to a person, not a ${target.record}, so ${target.entered} enters.${reEntryHint}`;
};

// Helper text under the trigger in the sidebar on job-based and placement-based campaigns.
// `isReEntryRestricted` is the campaign's re-entry restriction flag (automationStore.isAutomationReEntryRestriction).
export const surveyFormFanOutText = (triggerId, automationType, isReEntryRestricted = false) => {
  const target = fanOutTarget(triggerId, automationType);
  if (!target) return null;
  if (isReEntryRestricted && target.single)
    return `Surveys and forms are sent to a person, not a ${target.record}. When the person responds, only ${target.single} enters, because of this campaign's re-entry setting.`;
  return `Surveys and forms are sent to a person, not a ${target.record}. When the person responds, ${target.entered} enters.`;
};

export const SurveyFormIsNotText =
  "Is Not includes everyone in this campaign who has not done this within the time condition.";

// Copy of a trigger row whose id is the base Activity id when it is a job-based or placement-based
// Activity trigger, for code (such as the canvas hover) written against the base id.
export const withBaseActivityTriggerId = (item, automationType) =>
  item?.firstColumn && isJobPlacementActivityTrigger(Number(item.firstColumn.id), automationType)
    ? { ...item, firstColumn: { ...item.firstColumn, id: baseActivityTriggerId(automationType) } }
    : item;
